import { ref } from 'vue'
import { getAdditionalUserInfo, getAuth, GoogleAuthProvider, signInWithPopup, signOut } from 'firebase/auth'
// import { useCurrentUser, useFirebaseAuth } from 'vuefire'
import { logLogin, logSignUp } from '@/composables/useAnalytics'
import { useGoogleAccessToken } from '@/composables/useGoogleAccessToken'
import { logger } from '@/utils/logger'

// https://developers.google.com/identity/oauth2/web/guides/overview?authuser=0
// Automatic refresh of expired access tokens has been removed to improve user security and awareness.
// After an access token expires your app must handle Google API error responses, request, and obtain a new, valid access token.

const googleAuthProvider = new GoogleAuthProvider()

// You must call addScope for each scope separately; there is no method to add multiple scopes in one call.
googleAuthProvider.addScope('profile')
googleAuthProvider.addScope('email')
googleAuthProvider.addScope('https://www.googleapis.com/auth/drive.activity.readonly')
googleAuthProvider.addScope('https://www.googleapis.com/auth/drive.readonly')          // TODO: Access to Drive Content (`thumbnailLink`, comments...)
googleAuthProvider.addScope('https://www.googleapis.com/auth/drive.metadata.readonly')
googleAuthProvider.addScope('https://www.googleapis.com/auth/contacts.readonly')
googleAuthProvider.addScope('https://www.googleapis.com/auth/contacts.other.readonly')

googleAuthProvider.setCustomParameters({
    // include_granted_scopes: 'true', // to avoid asking for permissions that the user has already granted
    prompt: 'select_account', // to force account selection
    // access_type: 'offline', // to get a refresh token https://stackoverflow.com/questions/27890737/firebase-google-auth-offline-access-type-in-order-to-get-a-token-refresh
})

let accessToken = ''

/** True from the moment the Google popup sign-in succeeds until the app has finished loading the user's data */
export const postLoginPending = ref(false)

/*
export function reauthenticate () {
    const auth = getAuth() // only exists on client side
    if (!auth || !auth.currentUser) {
        logger.error('Auth not available or no current user')
        return
    }

    googleAuthProvider.setCustomParameters({
        login_hint: auth.currentUser.email || '', // to pre-fill the email field with the current user's email
        // include_granted_scopes: 'true', // to avoid asking for permissions that the user has already granted
        prompt: '', // silent re-authentication without prompting the user, if possible. If the user needs to reauthenticate, they will be prompted.
        // access_type: 'offline', // to get a refresh token https://stackoverflow.com/questions/27890737/firebase-google-auth-offline-access-type-in-order-to-get-a-token-refresh
    })

    reauthenticateWithPopup(auth.currentUser, googleAuthProvider)
        .then(async result => {
            const credential = GoogleAuthProvider.credentialFromResult(result)

            logger.log('Re-authenticated', result, credential)

            if (credential && credential.accessToken && !credential.accessToken.startsWith('Firebase')) {
                accessToken = credential.accessToken
                // 1 hour expiry minus 5 minutes buffer
                const expiresAt = Math.floor(Date.now() / 1000) + 3600 - 5 * 60
                const { setAccessToken } = useGoogleAccessToken()
                setAccessToken(accessToken, expiresAt)
                logger.log(credential.accessToken)
            }
        })
        .catch(error_ => {
            logger.error('Failed re-authentication', error_)
        })
}
*/

export function signIn (): Promise<boolean> {
    // const auth = useFirebaseAuth() // only exists on client side
    const auth = getAuth() // only exists on client side
    if (!auth) {
        logger.error('Auth not available')
        return Promise.resolve(false)
    }

    // auth.languageCode = 'it';
    // // To apply the default browser preference instead of explicitly setting it.
    // // auth.useDeviceLanguage();

    // TODO: googleAuthProvider.setCustomParameters({ access_type: 'offline' }) // save refresh token for long-term access https://stackoverflow.com/questions/27890737/firebase-google-auth-offline-access-type-in-order-to-get-a-token-refresh

    googleAuthProvider.setCustomParameters({
        // include_granted_scopes: 'true', // to avoid asking for permissions that the user has already granted
        prompt: 'select_account', // to force account selection
        // access_type: 'offline', // to get a refresh token https://stackoverflow.com/questions/27890737/firebase-google-auth-offline-access-type-in-order-to-get-a-token-refresh
    })

    return signInWithPopup(auth, googleAuthProvider)
        .then(async result => {
            postLoginPending.value = true
            const credential = GoogleAuthProvider.credentialFromResult(result)

            if (getAdditionalUserInfo(result)?.isNewUser) {
                logSignUp('google.com')
            } else {
                logLogin('google.com')
            }

            // logger.log('Signed in', result, credential)

            // skip if running in emulator because the emulator does not return a valid Google access token
            if (credential && credential.accessToken && !credential.accessToken.startsWith('Firebase')) {
                accessToken = credential.accessToken
                // 1 hour expiry minus 5 minutes buffer
                const expiresAt = Math.floor(Date.now() / 1000) + 3600 - 5 * 60
                const { setAccessToken } = useGoogleAccessToken()
                setAccessToken(accessToken, expiresAt)
                // await initializeGoogleAPI()
            }
            return true
        })
        .catch(error_ => {
            postLoginPending.value = false
            logger.error('Failed sign', error_)
            // error.value = error_
            return false
        })
}

export function logout () {
    postLoginPending.value = false
    return signOut(getAuth()).catch(error_ => {
        logger.error('Failed sign out', error_)
    })
}
